import type { CSSProperties } from 'react';
import type { DocumentSection } from '@shared/types';
import { knobsFor, knobValue } from '@shared/page-knobs';

/**
 * Page knobs (docs/composer-cards.md §13f): "renderers honor `section.style`
 * through classes and CSS variables on the existing type scale". This file
 * is the one place momentum_v3 turns a section's typed style overrides into
 * DOM hooks - every renderer calls `knobClasses`/`knobVars` and the actual
 * visual rules live in `web/src/styles/momentum-v3*.css`, keyed off the
 * classes below. `knobsFor`/`knobValue` (from the shared catalog,
 * `src/domain/page-knobs.ts`) already do the validation and default-
 * fallback work, so nothing here re-implements that: an unrecognized page
 * type or a stale/invalid stored value just falls through to their defaults.
 */
type KnobbedSection = Pick<DocumentSection, 'type' | 'style'> & {
  content?: Pick<DocumentSection['content'], 'layout'>;
};

/**
 * One class per knob the page type declares, `knob-<name>-<value>` - always
 * the full set for the page type, defaults included, so a plain unstyled
 * section still carries a stable class list (CSS keys off the default's
 * class exactly like an explicit override's). A page type with no catalog
 * entry (`knobsFor` returns []) yields no classes. `custom_page`'s catalog
 * depends on `content.layout` — see knobsFor's doc comment in
 * `src/domain/page-knobs.ts`.
 */
export function knobClasses(section: KnobbedSection): string[] {
  const layout = section.content?.layout;
  return knobsFor(section.type, layout).map(
    (def) => `knob-${def.knob}-${knobValue(section.type, section.style, def.knob, layout)}`,
  );
}

/**
 * `cover`'s `logoScale` steps (docs/composer-cards.md §13f "CoverPage"): S/M/
 * L/XL as a percentage of the brand panel's shorter side. A numeric scale
 * like this is cleaner as one CSS variable than four separate class rules
 * repeating the same percentages - `momentum-v3.css` reads
 * `--mv3-cover-logo-scale` on both the wordmark and the square-icon
 * fallback (`object-fit: contain` keeps either shape from stretching).
 */
const COVER_LOGO_SCALE_PCT: Record<string, number> = { S: 24, M: 38, L: 52, XL: 66 };

/**
 * CSS custom properties for the knob(s) better expressed as a variable than
 * a class switch. Every other knob (heading/statement size steps, density,
 * placement, tint) is a small, fixed number of discrete looks and is styled
 * entirely through the `knob-*` classes above instead.
 */
export function knobVars(section: KnobbedSection): CSSProperties {
  const vars: Record<string, string> = {};
  if (section.type === 'cover') {
    const scale = knobValue('cover', section.style, 'logoScale') ?? 'M';
    vars['--mv3-cover-logo-scale'] = `${COVER_LOGO_SCALE_PCT[scale] ?? COVER_LOGO_SCALE_PCT.M}%`;
  }
  return vars as CSSProperties;
}
